'use es6';

import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
import { Map as ImmutableMap, fromJS, Range } from 'immutable';
import I18n from 'I18n';
import http from 'hub-http/clients/apiClient';
import { DATE_FORMAT_BY_FREQUENCY, DATEINT_FORMAT, HUBSPOT_VIDEO_CRM_OBJECT_TYPE, RETENTION_REPORT_NAME } from '../constants';
import { fetchCrmVideoObjectsByIds } from '../crmObjects';
const VIEWS_REPORT_NAME = 'e_mb_media_played_on_page';
const getCrmObjectId = coordinate => Number(coordinate.split('-')[3]);
export const buildVideoObjectCoordinate = videoId => `${PortalIdParser.get()}-${HUBSPOT_VIDEO_CRM_OBJECT_TYPE}-${videoId}`;
export const toDateInt = date => Number(I18n.moment(date).format(DATEINT_FORMAT));
export const fromDateInt = dateInt => I18n.moment(String(dateInt), DATEINT_FORMAT);
export const formatDateLabel = (dateInt, {
  frequency
}) => {
  return fromDateInt(dateInt).format(DATE_FORMAT_BY_FREQUENCY[frequency]);
};
export function formatDuration(seconds) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${seconds % 60 < 10 ? '0' : ''}${seconds % 60}`;
}
function hydrateBuckets(pointsResp, crmSearchResp) {
  return pointsResp.map(bucket => {
    const objectId = getCrmObjectId(bucket.breakdown);
    const crmObjectForBucket = crmSearchResp.results.find(result => result.objectId === objectId);
    if (crmObjectForBucket && crmObjectForBucket.properties && crmObjectForBucket.properties.hs_title) {
      bucket.videoTitle = crmObjectForBucket.properties.hs_title.value;
    }
    bucket.views = bucket.counterValues[VIEWS_REPORT_NAME].value;
    return bucket;
  });
}
function hydrateCrmObjects(pointsResp) {
  if (!pointsResp.length) {
    return Promise.resolve(pointsResp);
  }
  const crmObjectIds = pointsResp.map(bucket => getCrmObjectId(bucket.breakdown));
  return fetchCrmVideoObjectsByIds(crmObjectIds).then(crmSearchResp => hydrateBuckets(pointsResp, crmSearchResp));
}
export function fetchViewsReport({
  crmPageObjectCoordinate,
  contentId,
  startDateInt,
  endDateInt,
  frequency,
  videoObjectCoordinates
}) {
  if (frequency && frequency.length > 1) {
    frequency = frequency[0].toLowerCase();
  }
  const data = {
    breakdown: '{properties.hs_media_bridge_object}',
    endDateInt,
    frequency,
    portalId: PortalIdParser.get(),
    reportType: VIEWS_REPORT_NAME,
    schemaVersion: enviro.isQa() ? 6 : 2,
    // todo remove when backfilled
    startDateInt,
    subAggregationsByTemplate: {}
  };
  if (crmPageObjectCoordinate) {
    data.subAggregationsByTemplate['{properties.hs_page_object_id}'] = crmPageObjectCoordinate;
  } else if (contentId) {
    data.subAggregationsByTemplate['{properties.hs_page_id}'] = contentId;
  }

  // only filter to specific videos if CosIndex returns any, until we're sure its stable for v2;
  if (videoObjectCoordinates && videoObjectCoordinates.length) {
    data.breakdownValues = videoObjectCoordinates;
  }
  return http.post('events/internal/v0/reports/points', {
    data
  }).then(hydrateCrmObjects).then(fromJS);
}
export function fetchRetentionReport({
  crmPageObjectCoordinate,
  contentId,
  startDateInt,
  endDateInt,
  videoObjectCoordinate
}) {
  const data = {
    breakdown: '{properties.hs_second_viewed}',
    endDateInt,
    frequency: 'd',
    portalId: PortalIdParser.get(),
    reportType: RETENTION_REPORT_NAME,
    schemaVersion: enviro.isQa() ? 4 : 2,
    startDateInt,
    subAggregationsByTemplate: {
      '{properties.hs_media_bridge_object}': videoObjectCoordinate
    }
  };
  if (crmPageObjectCoordinate) {
    data.subAggregationsByTemplate['{properties.hs_page_object_id}'] = crmPageObjectCoordinate;
  } else if (contentId) {
    data.subAggregationsByTemplate['{properties.hs_page_id}'] = contentId;
  }
  return http.post('events/internal/v0/reports/totals', {
    data
  }).then(resp => {
    // todo figure out sorting by chronological second on BE
    return fromJS(resp).map(bucket => bucket.update('breakdown', Number)).sortBy(bucket => bucket.get('breakdown'));
  });
}
export const getRetentionVisualization = reportData => {
  const breakdowns = reportData.map(bucket => bucket.get('breakdown'));
  const minBreakdownSecond = breakdowns.first();
  const maxBreakdownSecond = breakdowns.last();
  const range = Range(minBreakdownSecond, maxBreakdownSecond + 1).toList();
  const data = range.map(breakdownSecond => {
    const bucket = reportData.find(b => b.get('breakdown') === breakdownSecond);
    return ImmutableMap({
      breakdown: String(breakdownSecond).padStart(4, '0'),
      secondsLabel: formatDuration(breakdownSecond),
      viewsPerSecond: bucket ? bucket.getIn(['counterValues', RETENTION_REPORT_NAME, 'value']) : 0
    });
  });
  const dataSeriesProperties = {
    secondsLabel: {
      label: I18n.text('videoReports.videoRetentionChart.duration')
    },
    viewsPerSecond: {
      label: I18n.text('videoReports.videoRetentionChart.viewsPerSecond')
    }
  };
  return {
    data: {
      primary: {
        data,
        properties: dataSeriesProperties
      }
    },
    layers: [{
      dataset: 'primary',
      mark: {
        type: 'line'
      },
      x: {
        field: 'secondsLabel',
        type: 'category'
      },
      y: {
        field: 'viewsPerSecond',
        type: 'linear'
      }
    }],
    type: 'XY'
  };
};